import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Grid,
    Divider,
    TablePagination,
    Tabs,
    Tab,
    CircularProgress,
    LinearProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { listScenarios, Scenario, deleteScenario, getContractWorkspaces } from '../../api/scenarios';
import SearchableDropdown from '../../components/SearchableDropdown/SeachableDropdown';

const Scenarios: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get tab value from URL search params
    const getTabValueFromURL = (): number => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        return tabParam ? parseInt(tabParam, 10) : 0;
    };
    
    const [scenarios, setScenarios] = useState<Scenario[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [tabValue, setTabValue] = useState(getTabValueFromURL()); // Initialize from URL
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [scenarioToDelete, setScenarioToDelete] = useState<number | null>(null);
    const [contractWorkspaces, setContractWorkspaces] = useState<Array<{id: string, label: string}>>([]);
    const [selectedWorkspaces, setSelectedWorkspaces] = useState<Array<{id: string, label: string}>>([]);
    const [filtersInitialized, setFiltersInitialized] = useState(false);

    useEffect(() => {
        if (filtersInitialized) {
            loadScenarios();
        }
    }, [page, rowsPerPage, tabValue, selectedWorkspaces, filtersInitialized]);
    
    // Load contract workspaces for dropdown and check URL parameters
    useEffect(() => {
        const fetchContractWorkspaces = async () => {
            try {
                const workspaces = await getContractWorkspaces();
                setContractWorkspaces(workspaces);
                
                // Check URL for contract_ids or contract_id parameter
                const searchParams = new URLSearchParams(location.search);
                const contractIdsParam = searchParams.get('contract_ids');
                const contractIdParam = searchParams.get('contract_id');
                
                if (contractIdsParam) {
                    // Handle multiple contract IDs
                    const contractIds = contractIdsParam.split(',');
                    const selectedWorkspacesFromURL = workspaces.filter(ws => contractIds.includes(ws.id));
                    if (selectedWorkspacesFromURL.length > 0) {
                        setSelectedWorkspaces(selectedWorkspacesFromURL);
                        // Set tab to contract tab if we have contract IDs
                        setTabValue(1);
                    }
                } else if (contractIdParam) {
                    // Handle single contract ID
                    const selectedWorkspace = workspaces.find(ws => ws.id === contractIdParam);
                    if (selectedWorkspace) {
                        setSelectedWorkspaces([selectedWorkspace]);
                        // Set tab to contract tab if we have a contract ID
                        setTabValue(1);
                    }
                }
                
                // Mark filters as initialized to trigger data loading
                setFiltersInitialized(true);
            } catch (error) {
                console.error('Error fetching contract workspaces:', error);
            }
        };
        
        fetchContractWorkspaces();
    }, [location.search]);

    const loadScenarios = async () => {
        try {
            setLoading(true);
            const apiPage = page + 1; // API uses 1-based indexing
            
            // Determine filter based on tab value
            let filter;
            let contractIds: number[] | null = null;
            
            if (tabValue === 0) {
                filter = "NO_CONTRACT";
            } else {
                filter = "CONTRACT";
                
                if (selectedWorkspaces.length > 0) {
                    contractIds = selectedWorkspaces.map(ws => parseInt(ws.id));
                }
            }

            const response = await listScenarios(apiPage, rowsPerPage, contractIds, filter);
            
            // Ensure we have a valid response with items before updating state
            if (response && response.items) {
                setScenarios(response.items || []);
                setTotalItems(response.total || 0);
            } else {
                setScenarios([]);
                setTotalItems(0);
            }
        } catch (error) {
            console.error('Error loading scenarios:', error);
            setScenarios([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (scenarioId: number) => {
        navigate(`/homepage/scenarios/${scenarioId}`);
    };
    
    const handleDeleteClick = (scenarioId: number) => {
        setScenarioToDelete(scenarioId);
        setDeleteDialogOpen(true);
    };
    
    const handleDeleteConfirm = async () => {
        if (scenarioToDelete) {
            try {
                await deleteScenario(scenarioToDelete);
                // Reload scenarios after deletion
                loadScenarios();
            } catch (error) {
                console.error('Error deleting scenario:', error);
            }
        }
        setDeleteDialogOpen(false);
        setScenarioToDelete(null);
    };
    
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setScenarioToDelete(null);
    };

    const handleCreate = () => {
        navigate('/homepage/scenarios/new');
    };
    
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0); 
    };
    
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setPage(0); // Reset to first page when changing tabs
        setSelectedWorkspaces([]); // Clear selected workspaces when changing tabs
        
        // Update URL with new tab value without navigating
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('tab', newValue.toString());
        const newURL = `${location.pathname}?${newSearchParams.toString()}`;
        window.history.replaceState(null, '', newURL);
    };
    
    const handleWorkspaceSelect = (selectedIds: string[]) => {
        const selected = contractWorkspaces.filter(ws => selectedIds.includes(ws.id));
        setSelectedWorkspaces(selected);
        setPage(0); // Reset to first page when changing filter
        
        // Update URL with selected contract IDs
        const newSearchParams = new URLSearchParams(location.search);
        if (selectedIds.length > 0) {
            newSearchParams.set('contract_ids', selectedIds.join(','));
        } else {
            newSearchParams.delete('contract_ids');
        }
        newSearchParams.delete('contract_id'); // Remove single contract_id if it exists
        const newURL = `${location.pathname}?${newSearchParams.toString()}`;
        window.history.replaceState(null, '', newURL);
    };

    // We'll keep this empty as we'll handle loading states inline

    return (
        <Grid
            container
            sx={{ width: "100%", justifyContent: "center", height: "95vh"}}
        >
            <Grid
                sx={{
                    flex: 1,
                    mt: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    background: "#fff",
                    boxShadow:
                        "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
                    borderRadius: "8px",
                    width: "100%",
                }}
            >
                <Grid
                    container
                    sx={{ width: "100%" }}
                    justifyContent="space-between"
                >
                    <Typography
                        align="center"
                        sx={{ fontSize: "1.5rem", px: 2, p: 1.5 }}
                    >
                        Scenario Management
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                </Grid>

                <Grid
                    container
                    sx={{ width: "100%", p: 1.5 }}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item>
                        <Tabs 
                            value={tabValue} 
                            onChange={handleTabChange}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Generic Scenarios" />
                            <Tab label="Contract Scenarios" />
                        </Tabs>
                    </Grid>
                    
                    <Grid item container spacing={2} xs={12} md={6} justifyContent="flex-end" alignItems="center">
                        {tabValue === 1 && (
                            <Grid item xs={12} md={7}>
                                <Box display="flex" alignItems="center" gap="12px">
                                    <Typography variant="body2">Filter by Contract Workspace:</Typography>
                                    <SearchableDropdown
                                        options={contractWorkspaces}
                                        selectedValues={selectedWorkspaces.map(ws => ws.id)}
                                        onSelect={handleWorkspaceSelect}
                                        multiSelect={true}
                                    />
                                </Box>
                            </Grid>
                        )}
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleCreate}
                            >
                                Create New Scenario
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container sx={{ p: 1.5, pb: 5 }}>
                    <Grid xs={12}>
                        <TableContainer component={Paper}>
                            {loading && <LinearProgress />}
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Question</TableCell>
                                        {tabValue === 1 && <TableCell>Contract Workspace</TableCell>}
                                        <TableCell>Value Pool</TableCell> {/* Always show Value Pool */}
                                        {tabValue === 1 && <TableCell>Scope</TableCell>} {/* Added Scope Header */}
                                        <TableCell>Status</TableCell>
                                        <TableCell>Edit</TableCell>
                                        <TableCell>Delete</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && scenarios.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={tabValue === 1 ? 8 : 6} align="center"> {/* Updated colspan */}
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                    <CircularProgress size={24} />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : scenarios.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={tabValue === 1 ? 8 : 6} align="center"> {/* Updated colspan */}
                                                No scenarios found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        scenarios.map((scenario) => (
                                        <TableRow key={scenario.scenario_id}>
                                            <TableCell>{scenario.scenario_name}</TableCell>
                                            <TableCell>{scenario.question}</TableCell>
                                            {tabValue === 1 && <TableCell>{scenario.contract_workspace || '-'}</TableCell>}
                                            <TableCell>{scenario.category || '-'}</TableCell> {/* Always show Value Pool */}
                                            {tabValue === 1 && <TableCell>{scenario.scope || '-'}</TableCell>} {/* Added Scope Cell */}
                                            <TableCell>{scenario.is_active ? 'Active' : 'Inactive'}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEdit(scenario.scenario_id)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteClick(scenario.scenario_id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={totalItems}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelDisplayedRows={({ from, to, count }) => 
                                    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                                }
                                sx={{ mb: 3 }}
                            />
                        </TableContainer>
                    </Grid>
                </Grid>
            </Grid>
            
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Delete Scenario</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this scenario? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default Scenarios;
